.page {
  width: 100%;
}

.sectionHeader {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: var(--space-8);
  flex-wrap: wrap;
  gap: var(--space-4);
}

.sectionTitle {
  font-family: var(--font-heading);
  font-size: 28px;
  font-weight: 400;
  color: var(--color-text);
  letter-spacing: -0.01em;
}

.sectionSubtitle {
  font-family: var(--font-body);
  font-size: 14px;
  color: var(--color-text-dim);
  margin-top: var(--space-1);
}

@media (min-width: 768px) {
  .sectionTitle {
    font-size: 36px;
  }
}

/* Hero */
.hero {
  position: relative;
  min-height: 70vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--space-12) var(--container-padding);
  overflow: hidden;
}

@media (min-width: 768px) {
  .hero {
    min-height: 85vh;
    padding: var(--space-16) var(--container-padding);
  }
}

.heroBackground {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
  z-index: 0;
}

.mascot {
  width: 180px;
  height: 180px;
  opacity: 0.15;
  animation: float 6s ease-in-out infinite;
  filter: drop-shadow(0 20px 40px rgba(0,0,0,0.4));
}

.mascotLarge {
  width: 280px;
  height: 280px;
  opacity: 0.12;
}

@keyframes float {
  0%, 100% { transform: translateY(0) rotate(-2deg); }
  50% { transform: translateY(-20px) rotate(2deg); }
}

.heroContent {
  position: relative;
  z-index: 1;
  text-align: center;
  max-width: 800px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--space-6);
}

.heroBadge {
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-2) var(--space-4);
  font-family: var(--font-body);
  font-weight: 700;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--color-primary);
  background: rgba(90, 166, 66, 0.12);
  border: 1px solid rgba(90, 166, 66, 0.3);
  border-radius: var(--radius-pill);
  animation: pulseGlow 3s ease-in-out infinite;
}

@keyframes pulseGlow {
  0%, 100% { box-shadow: 0 0 8px rgba(90, 166, 66, 0.2); }
  50% { box-shadow: 0 0 20px rgba(90, 166, 66, 0.4); }
}

.badgeDot {
  width: 8px;
  height: 8px;
  background: var(--color-primary);
  border-radius: 50%;
  animation: blink 1.5s ease-in-out infinite;
}

@keyframes blink {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.4; }
}

.heroTitle {
  font-family: var(--font-heading);
  font-size: clamp(36px, 8vw, 64px);
  font-weight: 400;
  line-height: 1.1;
  color: var(--color-text);
  letter-spacing: -0.02em;
  text-shadow: 0 4px 20px rgba(0,0,0,0.5);
}

.highlight {
  background: linear-gradient(135deg, var(--color-primary) 0%, var(--color-gold) 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.heroSubtitle {
  font-family: var(--font-body);
  font-size: clamp(16px, 2.5vw, 20px);
  color: var(--color-text-dim);
  line-height: 1.6;
  max-width: 600px;
}

.heroActions {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: var(--space-4);
  margin-top: var(--space-2);
}

.heroStatus {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--space-4);
  flex-wrap: wrap;
  margin-top: var(--space-4);
}

.heroScrollHint {
  position: absolute;
  bottom: var(--space-8);
  left: 50%;
  transform: translateX(-50%);
  animation: bounce 2s ease-in-out infinite;
}

@keyframes bounce {
  0%, 20%, 50%, 80%, 100% { transform: translateX(-50%) translateY(0); }
  40% { transform: translateX(-50%) translateY(-10px); }
  60% { transform: translateX(-50%) translateY(-5px); }
}

.mouseIcon {
  width: 24px;
  height: 40px;
  color: var(--color-text-muted);
  opacity: 0.6;
}

/* Quick Stats */
.quickStats {
  padding: var(--space-10) var(--container-padding);
  background: linear-gradient(180deg, rgba(255,255,255,0.02) 0%, transparent 100%);
  border-top: 1px solid var(--color-border-dim);
  border-bottom: 1px solid var(--color-border-dim);
}

.statsGrid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: var(--space-4);
  max-width: var(--max-width);
  margin: 0 auto;
}

@media (min-width: 768px) {
  .statsGrid {
    grid-template-columns: repeat(4, 1fr);
    gap: var(--space-6);
  }
}

.statCard {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: var(--space-6) var(--space-4);
  background: var(--color-bg-panel);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  transition: all var(--transition-base);
}

.statCard:hover {
  border-color: var(--color-border-bright);
  transform: translateY(-4px);
  box-shadow: var(--shadow-lg);
}

.statIcon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  color: var(--color-primary);
  background: rgba(90, 166, 66, 0.12);
  border-radius: var(--radius-md);
  margin-bottom: var(--space-3);
}

.statSvg {
  width: 24px;
  height: 24px;
}

.statValue {
  font-family: var(--font-heading);
  font-size: 32px;
  font-weight: 400;
  color: var(--color-text);
  line-height: 1;
}

.statMax {
  font-family: var(--font-body);
  font-weight: 400;
  font-size: 16px;
  color: var(--color-text-muted);
}

.statLabel {
  font-family: var(--font-body);
  font-weight: 700;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--color-text-dim);
  margin-top: var(--space-2);
}

/* Features */
.features {
  padding: var(--space-16) var(--container-padding);
}

.featuresGrid {
  display: grid;
  grid-template-columns: 1fr;
  gap: var(--space-6);
  max-width: var(--max-width);
  margin: 0 auto;
}

@media (min-width: 640px) {
  .featuresGrid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (min-width: 1024px) {
  .featuresGrid {
    grid-template-columns: repeat(3, 1fr);
  }
}

.featureCard {
  height: 100%;
  transition: all var(--transition-base);
}

.featureCard:hover {
  transform: translateY(-4px);
  box-shadow: var(--shadow-lg);
  border-color: var(--color-border-bright);
}

.featureCardAlt {
  /* Alternate styling for second row */
}

.featureIcon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  color: var(--color-primary);
  background: rgba(90, 166, 66, 0.12);
  border-radius: var(--radius-md);
  margin-bottom: var(--space-4);
}

.featureSvg {
  width: 28px;
  height: 28px;
}

.featureTitle {
  font-family: var(--font-heading);
  font-size: 18px;
  font-weight: 400;
  color: var(--color-text);
  margin-bottom: var(--space-2);
}

.featureDesc {
  font-family: var(--font-body);
  font-size: 13px;
  color: var(--color-text-dim);
  line-height: 1.6;
}

/* Top Players Preview */
.topPlayers {
  padding: var(--space-16) var(--container-padding);
  background: var(--color-bg-base);
}

.playersPreview {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: var(--space-4);
  max-width: var(--max-width);
  margin: 0 auto;
}

@media (min-width: 768px) {
  .playersPreview {
    grid-template-columns: repeat(4, 1fr);
  }
}

.playerPreviewCard {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  text-decoration: none;
  padding: var(--space-5);
  background: var(--color-bg-panel);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  transition: all var(--transition-base);
  position: relative;
}

.playerPreviewCard:hover {
  border-color: var(--color-primary);
  transform: translateY(-4px);
  box-shadow: var(--shadow-lg), var(--shadow-glow-primary);
}

.rankNumber {
  position: absolute;
  top: -12px;
  left: 50%;
  transform: translateX(-50%);
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: var(--font-heading);
  font-size: 14px;
  font-weight: 400;
  color: var(--color-text-inverse);
  background: var(--color-gold);
  border: 2px solid var(--color-bg-base);
  border-radius: 50%;
  box-shadow: var(--shadow-sm);
  z-index: 1;
}

.rankNumber.rank-1 { background: #ffd700; }
.rankNumber.rank-2 { background: #c0c0c0; }
.rankNumber.rank-3 { background: #cd7f32; }
.rankNumber.rank-4 { background: var(--color-primary); }

.previewInfo {
  margin-top: var(--space-3);
  width: 100%;
}

.previewStats {
  font-family: var(--font-body);
  font-size: 11px;
  color: var(--color-text-muted);
}

/* Online Now */
.onlineNow {
  padding: var(--space-16) var(--container-padding);
}

.onlineGrid {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: var(--space-4);
  max-width: var(--max-width);
  margin: 0 auto;
}

.onlineCard {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-3) var(--space-4);
  background: var(--color-bg-panel);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-pill);
  text-decoration: none;
  transition: all var(--transition-fast);
}

.onlineCard:hover {
  border-color: var(--color-primary);
  transform: translateY(-2px);
  box-shadow: var(--shadow-md), var(--shadow-glow-primary);
}

.onlineDot {
  width: 8px;
  height: 8px;
  background: var(--color-online);
  border-radius: 50%;
  box-shadow: 0 0 8px var(--color-online);
  animation: pulse 2s ease-in-out infinite;
}

/* Rank Teaser */
.rankTeaser {
  padding: var(--space-16) var(--container-padding);
  background: linear-gradient(180deg, rgba(255,255,255,0.02) 0%, transparent 100%);
}

.rankCards {
  display: grid;
  grid-template-columns: 1fr;
  gap: var(--space-4);
  max-width: var(--max-width);
  margin: 0 auto var(--space-8);
}

@media (min-width: 640px) {
  .rankCards {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (min-width: 1024px) {
  .rankCards {
    grid-template-columns: repeat(4, 1fr);
  }
}

.rankPreviewCard {
  text-align: center;
  transition: all var(--transition-base);
}

.rankPreviewCard:hover {
  transform: translateY(-4px);
  box-shadow: var(--shadow-lg);
}

.rankPreviewHeader {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-4);
  border-bottom: 2px solid;
  margin-bottom: var(--space-3);
}

.rankPreviewName {
  font-family: var(--font-heading);
  font-size: 20px;
  font-weight: 400;
}

.rankPreviewPrice {
  font-family: var(--font-body);
  font-weight: 700;
  font-size: 14px;
  color: var(--color-gold);
}

.rankPreviewPerk {
  font-family: var(--font-body);
  font-size: 12px;
  color: var(--color-text-dim);
  line-height: 1.5;
}

.rankCTA {
  max-width: var(--max-width);
  margin: 0 auto;
  text-align: center;
}

/* CTA Section */
.cta {
  padding: var(--space-16) var(--container-padding);
  text-align: center;
}

.ctaContent {
  max-width: 600px;
  margin: 0 auto;
}

.ctaTitle {
  font-family: var(--font-heading);
  font-size: clamp(28px, 5vw, 42px);
  font-weight: 400;
  color: var(--color-text);
  margin-bottom: var(--space-4);
}

.ctaText {
  font-family: var(--font-body);
  font-size: 16px;
  color: var(--color-text-dim);
  line-height: 1.6;
  margin-bottom: var(--space-8);
}

.ctaActions {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: var(--space-4);
  margin-bottom: var(--space-6);
}

.ctaNote {
  font-family: var(--font-body);
  font-size: 12px;
  color: var(--color-text-muted);
}

.ctaNote a {
  color: var(--color-cyan);
}

.ctaNote a:hover {
  color: var(--color-gold);
}

/* Responsive adjustments */
@media (max-width: 639px) {
  .hero {
    min-height: 60vh;
  }

  .heroActions {
    flex-direction: column;
    align-items: center;
  }

  .heroActions :global(.mc-button) {
    width: 100%;
    max-width: 280px;
  }
}